import React, { FC, useEffect, useRef, useState } from 'react';
import { getTestingAttributes } from 'app/functions';
import { elements } from 'app/tests/elements';
import cn from 'classnames';
import { isBefore, startOfDay, addMonths, isSameDay } from 'date-fns';
import { emitFieldClosedEvent, emitFieldOpenedEvent } from 'app/events';
import { FIELD_DEPARTURE_DATE, FIELD_RETURN_DATE } from 'app/storage/fieldNames';
import { breakpointMatches } from 'app/functions/grid';
import { DateInput } from 'app/components/date/DateInput';
import * as styles from './DateRangePicker.scss';
import { useAppSelector } from 'app/hooks/appHooks';
import { focusedPartSelector } from 'app/store/selectors';
import { emitSearchMaskDeleteReturnClickEvent } from 'app/events/emitDeleteReturnClickEvent';
import { startOfMonth } from 'date-fns';
import { PartWrapper } from 'app/containers/PartWrapper/PartWrapper';
import { DateRangePickerContent } from 'app/components/date/DateRangePickerContent/DateRangePickerContent';
import { useOutsideClick } from 'app/hooks/useClickOutside';
import { DateMobilePanel } from 'app/components/date/DateMobilePanel/DateMobilePanel';
import { closeElement } from 'app/functions/general/closeElement';
import { useTranslations } from 'app/hooks/useTranslations';
import { a11yLabelsKeys } from 'app/providers/a11yLabels';
import { updateFocusedPart } from 'app/store/reducers/ui';
import { useAppDispatch } from 'app/hooks/appHooks';

export type DateRangePickerProps = {
  departureDate: Date;
  minDepartureDate: Date;
  returnDate: Date;
  returnDateVisible: boolean;
  onDepartureDateChange: (date: Date) => void;
  onReturnDateChange: (date: Date) => void;
  onHideReturnDate: () => void;
};

export const DateRangePicker: FC<DateRangePickerProps> = ({
  departureDate,
  minDepartureDate,
  returnDate,
  returnDateVisible,
  onDepartureDateChange,
  onReturnDateChange,
  onHideReturnDate,
}) => {
  const [departureFocused, setDepartureFocused] = useState(false);
  const [returnFocused, setReturnFocused] = useState(false);
  const [scrolledToView, setScrolledToView] = useState(false);
  const [monthScroll, setMonthScroll] = useState<number>(0);
  const departureInput = useRef<HTMLInputElement>(null);
  const returnInput = useRef<HTMLInputElement>(null);
  const lastFocused = useRef<'departure' | 'arrival' | null>(null);
  const dropdown = useRef<HTMLDivElement>(null);
  const focusedPart = useAppSelector(focusedPartSelector);
  const dispatch = useAppDispatch();
  const [isPanelOpened, setIsPanelOpened] = useState(false);

  const focused = departureFocused || returnFocused;

  const {
    getTranslationDepartureDateLabel,
    getTranslationReturnDateLabel,
    getDialogAriaLabelTo,
    getDialogAriaLabelFrom,
  } = useTranslations({
    getTranslationDepartureDateLabel: 'depart',
    getTranslationReturnDateLabel: 'back',
    getDialogAriaLabelTo: a11yLabelsKeys.dates.arrivalDialog,
    getDialogAriaLabelFrom: a11yLabelsKeys.dates.departureDialog,
  });

  useEffect(() => {
    if (breakpointMatches('small').matches && focused && !scrolledToView) {
      scrollIntoSelectedDate();
    }
  }, [focused, scrolledToView]);

  useEffect(() => {
    if (focusedPart !== 'date') {
      close();
    }
  }, [focusedPart]);

  const isSmallBreakpoint = breakpointMatches('small').matches;

  const close = () => {
    setMonthScroll(0);
    if (departureFocused) {
      handleDepartureChange(false);
    }
    if (returnFocused) {
      handleReturnChange(false);
    }

    setScrolledToView(false);
  };

  const afterCloseAction = () => {
    if (isSmallBreakpoint) {
      return;
    }
    if (returnFocused) {
      returnInput.current?.focus();
    } else {
      departureInput.current?.focus();
    }
  };

  useOutsideClick(dropdown, () => {
    if (departureFocused) {
      setDepartureFocused(false);
      closeElement(FIELD_DEPARTURE_DATE);
    }
    if (returnFocused) {
      setReturnFocused(false);
      closeElement(FIELD_RETURN_DATE);
    }
  });

  const handleDepartureDateChange = (date: Date) => {
    setMonthScroll(0);

    if (!isSameDay(date, departureDate)) {
      onDepartureDateChange(date);
    }
  };

  const handleReturnDateChange = (date: Date) => {
    if (!isSameDay(date, returnDate)) {
      onReturnDateChange(date);
    }
  };

  const scrollToElement = (element: string) => {
    const currentElement = document.querySelector(`[class*="hcr-clndr__btn--${element}"]`);

    currentElement
      ?.closest('[class*="hcr-clndr__table"]')
      ?.querySelector('[class*="hcr-clndr__caption"]')
      ?.scrollIntoView();
  };

  const scrollIntoSelectedDate = () => {
    setTimeout(() => {
      if (!returnDateVisible) {
        scrollToElement('selected');
      } else {
        scrollToElement(returnFocused ? 'end' : 'start');
      }
      setScrolledToView(true);
    });
  };

  const handleDepartureChange = (isFocused: boolean) => {
    if (isFocused) {
      emitFieldOpenedEvent(FIELD_DEPARTURE_DATE);
      lastFocused.current = 'departure';
      if (focusedPart !== 'date') dispatch(updateFocusedPart('date'));
    } else if (!returnDateVisible) {
      if (isSmallBreakpoint) {
        emitFieldClosedEvent(FIELD_DEPARTURE_DATE);
      } else {
        closeElement(FIELD_DEPARTURE_DATE);
      }
    }

    setDepartureFocused(isFocused);
  };

  const handleReturnChange = (isFocused: boolean, emit = true) => {
    if (isFocused) {
      lastFocused.current = 'arrival';
    }

    if (isFocused && focusedPart !== 'date') {
      dispatch(updateFocusedPart('date'));
    }

    if (isFocused && emit) {
      emitFieldOpenedEvent(FIELD_RETURN_DATE);
    } else if (emit) {
      if (isSmallBreakpoint) {
        emitFieldClosedEvent(FIELD_RETURN_DATE);
      } else {
        closeElement(FIELD_RETURN_DATE);
      }
    }

    setReturnFocused(isFocused);
  };

  const openDepartureDate = (e?: React.SyntheticEvent) => {
    setDepartureFocused(true);
    setIsPanelOpened(true);
    handleDepartureChange(true);
    if (returnFocused) {
      handleReturnChange(false);
    }

    if (e) {
      e.preventDefault(); // prevent focusing on this field
    }
  };

  const openReturnDate = (e?: React.SyntheticEvent) => {
    setIsPanelOpened(true);
    handleReturnChange(true);
    if (departureFocused) {
      handleDepartureChange(false);
    }

    if (e) {
      e.preventDefault(); // prevent focusing on this field
    }
  };

  const validDayForClick = (date: Date) =>
    !isBefore(startOfDay(date), startOfDay(minDepartureDate));

  const getIsBefore = (date1: Date, date2: Date) =>
    isBefore(addMonths(startOfMonth(date1), -monthScroll), startOfMonth(date2));

  const getIsPreviousButton = () =>
    returnFocused
      ? getIsBefore(departureDate, returnDate)
      : getIsBefore(minDepartureDate, departureDate);

  const afterSelectFocusAction = ({ focusedItem }: { focusedItem: 'departure' | 'arrival' }) => {
    if (!breakpointMatches('small').matches) {
      (focusedItem === 'departure' ? departureInput.current : returnInput.current)?.focus();
    } else {
      document.getElementById('confirm-date-button')?.focus();
    }
  };

  const onCloseAction = () => {
    close();
    afterCloseAction();
  };

  const contentProps = {
    getIsPreviousButton,
    setMonthScroll,
    monthScroll,
    minDepartureDate,
    departureDate,
    returnDate,
    departureFocused,
    returnFocused,
    returnDateVisible,
    returnInput,
    handleDepartureChange,
    handleReturnChange,
    handleDepartureDateChange,
    handleReturnDateChange,
    afterSelectFocusAction,
  };

  return (
    <div ref={dropdown}>
      <PartWrapper extraClasses={styles.dateRangeWrapper} onOpen={() => {}} onClose={onCloseAction}>
        <div
          className={cn(styles.dateInput, styles.dateInputDeparture, {
            [styles.wideInput]: !returnDateVisible,
          })}
        >
          <DateInput
            {...getTestingAttributes(elements.DEPARTURE_DATE_INPUT_FIELD)}
            ref={departureInput}
            date={departureDate}
            label={getTranslationDepartureDateLabel()}
            showTooltipArrow={departureFocused && !isSmallBreakpoint}
            isExpanded={departureFocused}
            onClick={departureFocused && !isSmallBreakpoint ? onCloseAction : openDepartureDate}
            valid={validDayForClick(departureDate)}
            returnDateVisible={returnDateVisible}
            onDateChange={handleDepartureDateChange}
          />
        </div>

        {returnDateVisible && (
          <div className={`${styles.dateInput} ${styles.dateInputReturn}`}>
            <DateInput
              {...getTestingAttributes(elements.ARRIVAL_DATE_INPUT_FIELD)}
              ref={returnInput}
              date={returnDate}
              isDepartureFocused={departureFocused}
              label={getTranslationReturnDateLabel()}
              showTooltipArrow={returnFocused && !isSmallBreakpoint}
              isExpanded={returnFocused}
              onClick={returnFocused && !isSmallBreakpoint ? onCloseAction : openReturnDate}
              isArrival={true}
              onCrossButtonClick={() => {
                onHideReturnDate();
                close();
                emitSearchMaskDeleteReturnClickEvent();
                afterCloseAction();
              }}
              onDateChange={handleReturnDateChange}
            />
          </div>
        )}

        <br />
        <div
          id={`dialog-date-${returnFocused ? 'to' : 'from'}`}
          aria-hidden={!returnFocused && !departureFocused}
          style={{ width: '100%' }}
        >
          {(departureFocused || returnFocused) && !isSmallBreakpoint && (
            <div
              className={styles.dateRangeContent}
              aria-label={returnFocused ? getDialogAriaLabelTo() : getDialogAriaLabelFrom()}
              role="dialog"
              aria-live="polite"
            >
              <DateRangePickerContent {...contentProps} />
            </div>
          )}
          {(departureFocused || returnFocused) && isPanelOpened && isSmallBreakpoint && (
            <DateMobilePanel
              isActive={departureFocused || returnFocused}
              onClose={() => {
                setIsPanelOpened(false);
                close();
                if (returnFocused) {
                  returnInput.current?.focus();
                } else {
                  departureInput.current?.focus();
                  if (returnDateVisible) {
                    emitFieldClosedEvent(FIELD_DEPARTURE_DATE);
                  }
                }
              }}
              title={
                departureFocused
                  ? getTranslationDepartureDateLabel()
                  : getTranslationReturnDateLabel()
              }
            >
              <DateRangePickerContent {...contentProps} />
            </DateMobilePanel>
          )}
        </div>
      </PartWrapper>
    </div>
  );
};
